import { useState, useRef, useEffect } from "react";

import Pack from "../Pack";

import heroImage from "../../img/heatmaps/hero_banner_new.png";
import heroImageHeatmap from "../../img/heatmaps/heatmap_hero_banner_new.png";
import heroAfterImage from "../../img/heatmaps/hero_after_banner_new.png";
import heroAfterImageHeatmap from "../../img/heatmaps/heatmap_hero_after_banner_new.png";
import handler from "../../img/heatmaps/handler.svg";
import styles from "../../css/heatmaps/ImageSliderHM.module.css";

import { PiCursorClickBold } from "react-icons/pi";
import { BsHandIndexThumb } from "react-icons/bs";
import TipsFromAnalysis from "./TipsFromAnalysis";

const ImageSliderHM = () => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [animationClass, setAnimationClass] = useState("");
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [activeBtn, setActiveBtn] = useState("before");
  const containerRef = useRef(null);
  const handlerRef = useRef(null);

  useEffect(() => {
    const checkForTouch = () => {
      return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    };
    setIsTouchDevice(checkForTouch());
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setAnimationClass(styles.isAnimating);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.7 }
    );
    const currentContainerRef = containerRef.current;
    if (currentContainerRef) {
      observer.observe(currentContainerRef);
    }
    return () => {
      if (currentContainerRef) {
        observer.unobserve(currentContainerRef);
      }
    };
  }, []);

  useEffect(() => {
    const currentHandler = handlerRef.current;
    const handleAnimationEnd = () => {
      setAnimationClass("");
    };
    if (animationClass && currentHandler) {
      currentHandler.addEventListener("animationend", handleAnimationEnd, {
        once: true,
      });
    }
    return () => {
      if (currentHandler) {
        currentHandler.removeEventListener("animationend", handleAnimationEnd);
      }
    };
  }, [animationClass]);

  const handleInteractionStart = (e) => {
    if (animationClass) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleInteractionMove = (e) => {
    if (!isDragging || animationClass) return;
    const container = containerRef.current;
    if (!container) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    let newPosition = (x / rect.width) * 100;
    if (newPosition < 0) newPosition = 0;
    if (newPosition > 100) newPosition = 100;
    setSliderPosition(newPosition);
  };

  const handleInteractionEnd = () => {
    setIsDragging(false);
  };

  // Prevent re-triggering animation when toggling to tips and back
  useEffect(() => {
    if (activeBtn === "tips" && animationClass) {
      setAnimationClass("");
    }
  }, [activeBtn, animationClass]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleInteractionMove);
      window.addEventListener("mouseup", handleInteractionEnd);
      window.addEventListener("touchmove", handleInteractionMove);
      window.addEventListener("touchend", handleInteractionEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleInteractionMove);
      window.removeEventListener("mouseup", handleInteractionEnd);
      window.removeEventListener("touchmove", handleInteractionMove);
      window.removeEventListener("touchend", handleInteractionEnd);
    };
  }, [isDragging]);

  const handleBeforeAfterClick = (variant) => {
    setActiveBtn(variant);
  };

  return (
    <Pack dataComponent={"image-slider-heatmaps"}>
      <div className={styles.container}>
        <h2 className="title">Porovnání výsledků s PermoniQ Heatmapou</h2>
        <div
          className={styles.beforeAfterBox}
          role="tablist"
          aria-label="Přepínač zobrazení"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeBtn === "before"}
            className={`${styles.beforeAfterBtn} ${
              activeBtn === "before" ? styles.active : ""
            }`}
            onClick={() => handleBeforeAfterClick("before")}
          >
            Banner před analýzou
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeBtn === "after"}
            className={`${styles.beforeAfterBtn} ${
              activeBtn === "after" ? styles.active : ""
            }`}
            onClick={() => handleBeforeAfterClick("after")}
          >
            Banner po analýze
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeBtn === "tips"}
            className={`${styles.beforeAfterBtn} ${
              activeBtn === "tips" ? styles.active : ""
            }`}
            onClick={() => handleBeforeAfterClick("tips")}
          >
            Doporučení pro úpravu
          </button>
        </div>
        <div
          className={`${styles.sliderContainer} ${animationClass}`}
          ref={containerRef}
        >
          <div className={styles.imageBox}>
            {/* Tips content kept mounted, toggled by display to avoid flicker */}
            <div style={{ display: activeBtn === "tips" ? "block" : "none" }}>
              <TipsFromAnalysis />
            </div>

            {/* Slider content kept mounted, toggled by display to avoid flicker */}
            <div style={{ display: activeBtn !== "tips" ? "block" : "none" }}>
              {/* Base images: keep both in DOM, switch by display */}
              <img
                src={heroImage}
                width={1280}
                height={500}
                alt="Původní snímek"
                style={{ display: activeBtn === "before" ? "block" : "none" }}
              />
              <img
                src={heroAfterImage}
                width={1280}
                height={500}
                alt="Snímek po úpravě"
                style={{ display: activeBtn === "after" ? "block" : "none" }}
              />

              <div
                className={styles.imageWrapper}
                style={
                  !animationClass
                    ? { width: `${100 - sliderPosition}%` }
                    : undefined
                }
              >
                {/* Overlay images: keep both in DOM, switch by display */}
                <img
                  src={heroImageHeatmap}
                  width={1280}
                  height={500}
                  alt="Snímek s heatmapou před analýzou"
                  style={{ display: activeBtn === "before" ? "block" : "none" }}
                />
                <img
                  src={heroAfterImageHeatmap}
                  width={1280}
                  height={500}
                  alt="Snímek s heatmapou po analýze"
                  style={{ display: activeBtn === "after" ? "block" : "none" }}
                />
              </div>
            </div>
          </div>
          {/* Keep handler mounted; hide in tips to avoid slider UI */}
          <div
            ref={handlerRef}
            className={styles.handler}
            style={{
              ...(animationClass ? {} : { left: `${sliderPosition}%` }),
              display: activeBtn !== "tips" ? "flex" : "none",
            }}
            onMouseDown={handleInteractionStart}
            onTouchStart={handleInteractionStart}
          >
            <img src={handler} alt="Posuvník" />
            {animationClass &&
              (isTouchDevice ? (
                <BsHandIndexThumb className={styles.dragHandIcon} />
              ) : (
                <PiCursorClickBold className={styles.dragHintIcon} />
              ))}
          </div>
        </div>
      </div>
    </Pack>
  );
};

export default ImageSliderHM;
